import React, { useEffect, useState } from "react";
import Icon from "@mdi/react";
import { RefreshCw } from "lucide-react";
import {
  mdiChevronDown,
  mdiChevronRight,
  mdiCloseCircleOutline,
  mdiEyeOutline,
  mdiFileDocumentOutline,
  mdiReceipt,
  mdiUploadOutline,
} from "@mdi/js";
import {
  WarehouseOutwardModel,
  WarehouseOutwardStatus,
} from "../model/warehouse_outward_model";
import {
  formatAddress,
  getOutwardDestinationAddress,
  getOutwardDestinationLabel,
  getOutwardSourceLabel,
} from "../warehouse_helper";
import PaginationInfo from "../common-components/PaginationInfo";
import { MobileCard, MobileField } from "../common-components/MobileCard";

interface PendingActionsTableProps {
  outwards: WarehouseOutwardModel[];
  loading: boolean;
  actionsAllowed?: boolean;
  itemSearch?: string;
  cascade?: boolean;
  sortBy?: string;
  sortOrder?: string;
  currentPage?: number;
  totalRecords?: number;
  limit?: number;
  onSortByValue?: () => void;
  onViewClick?: (outward: WarehouseOutwardModel) => void;
  onGenerateDCClick?: (outward: WarehouseOutwardModel) => void;
  onUploadInvClick?: (outward: WarehouseOutwardModel) => void;
  onUploadReverseDcDocsClick?: (outward: WarehouseOutwardModel) => void;
  onUploadInvoiceClick?: (outward: WarehouseOutwardModel) => void;
  onToggleEwayClick?: (outward: WarehouseOutwardModel, waived: boolean) => void;
  onRejectClick?: (outward: WarehouseOutwardModel) => void;
}

const getTotalValueFromItems = (o: WarehouseOutwardModel): number => {
  return (
    o.items?.reduce((sum, i) => sum + (i.itemPrice || 0) * i.requestedQty, 0) ??
    0
  );
};

const isHighValue = (value: number) => value >= 50000;

const getRequiresLabel = (o: WarehouseOutwardModel) => {
  const totalValue = getTotalValueFromItems(o);
  if (isHighValue(totalValue)) {
    if (o.type === "SO" && o.eway_waived) return "Inv ×2 (E-way waived)";
    return "DC + Inv/E-way";
  }
  return "Create DC";
};

interface PendingItemsSubTableProps {
  title: string;
  rows: Array<{
    itemName?: string;
    inventoryItemCode?: string;
    requestedQty?: number;
    itemPrice?: number;
  }>;
  rowKeyPrefix: string;
  isItemMatch: (it: {
    itemName?: string;
    inventoryItemCode?: string;
  }) => boolean;
}

const PendingItemsSubTable: React.FC<PendingItemsSubTableProps> = ({
  title,
  rows,
  rowKeyPrefix,
  isItemMatch,
}) => (
  <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
    <div className="px-4 py-2 text-sm font-semibold text-gray-700 border-b bg-gray-50">
      {title} ({rows.length})
    </div>
    <table className="w-full text-sm">
      <thead className="bg-white border-b">
        <tr>
          <th className="px-4 py-2 text-left font-semibold text-gray-600">
            Item Name
          </th>
          <th className="px-4 py-2 text-left font-semibold text-gray-600">
            Item Code
          </th>
          <th className="px-4 py-2 text-right font-semibold text-gray-600">
            Qty
          </th>
          <th className="px-4 py-2 text-right font-semibold text-gray-600">
            Unit Price
          </th>
          <th className="px-4 py-2 text-right font-semibold text-gray-600">
            Subtotal
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((it, idx) => {
          const qty = it.requestedQty ?? 0;
          const price = it.itemPrice ?? 0;
          const subtotal = qty * price;
          const matched = isItemMatch(it);
          const cellBg = matched ? "bg-green-100" : "";
          return (
            <tr
              key={`${rowKeyPrefix}-${idx}`}
              className={`border-b last:border-b-0 ${
                matched ? "bg-green-100 border-l-4 border-l-green-500" : ""
              }`}
            >
              <td className={`px-4 py-2 text-gray-800 ${cellBg}`}>
                {it.itemName || "--"}
              </td>
              <td className={`px-4 py-2 text-gray-500 ${cellBg}`}>
                {it.inventoryItemCode || "--"}
              </td>
              <td
                className={`px-4 py-2 text-right font-semibold text-gray-800 ${cellBg}`}
              >
                {qty}
              </td>
              <td className={`px-4 py-2 text-right text-gray-700 ${cellBg}`}>
                ₹{price.toLocaleString()}
              </td>
              <td
                className={`px-4 py-2 text-right font-semibold text-gray-800 ${cellBg}`}
              >
                ₹{subtotal.toLocaleString()}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const getRequiresConfig = (o: WarehouseOutwardModel) => {
  const totalValue = getTotalValueFromItems(o);
  const highValue = isHighValue(totalValue);

  return {
    icon: highValue ? mdiReceipt : mdiFileDocumentOutline,
    className: highValue
      ? "bg-purple-100 text-purple-800"
      : "bg-blue-100 text-blue-800",
  };
};

const EwayToggle: React.FC<{
  outward: WarehouseOutwardModel;
  onToggle?: (outward: WarehouseOutwardModel, waived: boolean) => void;
  id?: string;
}> = ({ outward, onToggle, id }) => {
  const waived = !!outward.eway_waived;
  const bothInvoices = !!(
    outward.invoice_attachment && outward.invoice_material_attachment
  );

  return (
    <div
      className="inline-flex items-center gap-1.5"
      title={
        bothInvoices
          ? "E-way bill required for this SO. Turn off to waive (e.g. split sub-₹50k invoices on one vehicle)."
          : "E-way bill required. You can waive it once both invoices are received."
      }
    >
      <span className="text-xs font-semibold tracking-wide text-gray-500">
        EWB
      </span>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={!waived}
        disabled={!bothInvoices}
        onClick={() => onToggle?.(outward, !waived)}
        className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
          waived ? "bg-gray-300" : "bg-blue-600"
        } ${!bothInvoices ? "cursor-not-allowed opacity-50" : ""}`}
      >
        <span
          className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${
            waived ? "translate-x-0.5" : "translate-x-3.5"
          }`}
        />
      </button>
    </div>
  );
};

const PendingActionsTable: React.FC<PendingActionsTableProps> = ({
  outwards,
  loading,
  actionsAllowed = true,
  itemSearch,
  cascade = false,
  sortBy,
  sortOrder,
  currentPage,
  totalRecords,
  limit,
  onSortByValue,
  onViewClick,
  onGenerateDCClick,
  onUploadInvClick,
  onUploadReverseDcDocsClick,
  onToggleEwayClick,
  onRejectClick,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpandedRows(new Set());
  }, [cascade]);

  const toggleExpanded = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const itemSearchTokens = (itemSearch || "")
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const isItemMatch = (it: { itemName?: string; inventoryItemCode?: string }) =>
    itemSearchTokens.length > 0 &&
    itemSearchTokens.some(
      (tok) =>
        (it.itemName || "").toLowerCase().includes(tok) ||
        (it.inventoryItemCode || "").toLowerCase().includes(tok),
    );

  const sortArrow =
    sortBy === "value" ? (sortOrder === "asc" ? "▲" : "▼") : "↕";

  return (
    <div
      id="warehouse-finance-pending-actions-table"
      className="rounded-lg border bg-card shadow-sm bg-white"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold">Orders Awaiting Documents</h3>
          {totalRecords !== undefined &&
            currentPage !== undefined &&
            limit !== undefined && (
              <PaginationInfo
                totalRecords={totalRecords}
                currentPage={currentPage}
                limit={limit}
              />
            )}
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Generate Delivery Challan for pending orders or reject if there are
          issues
        </p>

        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="px-4 py-3 text-left font-medium">Order ID</th>
                <th className="px-4 py-3 text-left font-medium">Type</th>
                <th className="px-4 py-3 text-left font-medium hidden md:table-cell">
                  From WH
                </th>
                <th className="px-4 py-3 text-left font-medium hidden lg:table-cell">
                  Destination
                </th>
                <th className="px-4 py-3 text-left font-medium hidden lg:table-cell">
                  Customer Group
                </th>
                <th className="px-4 py-3 text-left font-medium hidden lg:table-cell">
                  Customer Name
                </th>
                <th className="px-4 py-3 text-left font-medium hidden xl:table-cell">
                  Contact
                </th>
                {/* <th className="px-4 py-3 text-left font-medium hidden xl:table-cell">
                  Email
                </th> */}
                <th className="px-4 py-3 text-right font-medium">
                  <button
                    id="warehouse-finance-pending-sort-value-btn"
                    type="button"
                    onClick={onSortByValue}
                    className="inline-flex items-center gap-1 hover:text-gray-800"
                  >
                    Value
                    <span
                      className={
                        sortBy === "value" ? "text-blue-600" : "text-gray-400"
                      }
                    >
                      {sortArrow}
                    </span>
                  </button>
                </th>
                <th className="px-4 py-3 text-left font-medium">Requires</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={12}
                    className="px-4 py-6 text-center text-sm text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && outwards.length === 0 && (
                <tr>
                  <td
                    colSpan={12}
                    className="px-4 py-6 text-center text-sm text-gray-500"
                  >
                    No pending documents found
                  </td>
                </tr>
              )}
              {!loading &&
                outwards.map((outward) => {
                  const config = getRequiresConfig(outward);
                  const totalValue = getTotalValueFromItems(outward);
                  const highValue = isHighValue(totalValue);

                  const items = outward.items || [];
                  const faultyItems = (outward as any).faulty_items || [];
                  const hasItems = items.length > 0 || faultyItems.length > 0;
                  const hasMatchedItem =
                    items.some(isItemMatch) || faultyItems.some(isItemMatch);
                  const rowId = outward._id || outward.id;
                  const isExpanded =
                    expandedRows.has(rowId) || hasMatchedItem || cascade;

                  return (
                    <React.Fragment key={rowId}>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {hasItems ? (
                              <button
                                id={`warehouse-finance-pending-row-${rowId}-expand-btn`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpanded(rowId);
                                }}
                                className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-gray-100 text-gray-500"
                                aria-label={
                                  isExpanded ? "Collapse items" : "Expand items"
                                }
                              >
                                <Icon
                                  path={
                                    isExpanded
                                      ? mdiChevronDown
                                      : mdiChevronRight
                                  }
                                  size={0.7}
                                />
                              </button>
                            ) : (
                              <span className="inline-block h-6 w-6" />
                            )}
                            <span>
                              {outward.type === "SO"
                                ? outward.order_id
                                : outward.id}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {outward.type === "ST" &&
                          "st_subtype" in outward &&
                          (outward.st_subtype === "Returnable" ||
                            outward.st_subtype === "Replacement" ||
                            outward.st_subtype === "FoC" ||
                            outward.st_subtype === "Reverse DC") ? (
                            <div className="flex flex-col gap-1">
                              <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-secondary">
                                {outward.type}
                              </span>
                              <div
                                className={`inline-flex items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 gap-1 font-medium text-[10px] px-1.5 py-0 ${
                                  outward.st_subtype === "Replacement"
                                    ? "bg-yellow-100 text-yellow-600 border-yellow-300"
                                    : outward.st_subtype === "FoC"
                                      ? "bg-green-100 text-green-600 border-green-300"
                                      : outward.st_subtype === "Reverse DC"
                                        ? "bg-red-100 text-red-700 border-red-300"
                                        : "bg-blue-100 text-blue-600 border-blue-300"
                                }`}
                              >
                                <RefreshCw className="h-2.5 w-2.5" />
                                {outward.st_subtype}
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-secondary">
                              {outward.type}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-gray-600">
                          {getOutwardSourceLabel(outward)}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-gray-600">
                          {getOutwardDestinationLabel(outward)}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-gray-600">
                          {outward.customerDetails?.customer_group || "--"}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-gray-600">
                          {[
                            outward.customerDetails?.first_name,
                            outward.customerDetails?.last_name,
                          ]
                            .filter(Boolean)
                            .join(" ") || "--"}
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell text-gray-600 whitespace-nowrap">
                          {outward.customerDetails?.mobile || "--"}
                        </td>
                        {/* <td className="px-4 py-3 hidden xl:table-cell text-gray-600">
                          {outward.customerDetails?.email || "--"}
                        </td> */}
                        <td className="px-4 py-3 text-right font-medium">
                          <span className={highValue ? "text-yellow-600" : ""}>
                            ₹{totalValue.toLocaleString()}
                          </span>
                          {highValue && (
                            <div
                              className={`text-xs ${
                                outward.type === "SO" && outward.eway_waived
                                  ? "text-gray-500"
                                  : "text-yellow-600"
                              }`}
                            >
                              {outward.type === "SO" && outward.eway_waived
                                ? "E-way waived"
                                : "E-way required"}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.className}`}
                          >
                            <Icon path={config.icon} size={0.6} />
                            {getRequiresLabel(outward)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 border-purple-200">
                            {outward.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col items-end gap-1.5">
                            {actionsAllowed &&
                              outward.type === "SO" &&
                              highValue && (
                                <EwayToggle
                                  id={`warehouse-finance-pending-row-${rowId}-eway-toggle`}
                                  outward={outward}
                                  onToggle={onToggleEwayClick}
                                />
                              )}
                            <div className="flex items-center justify-end gap-2">
                              <button
                                id={`warehouse-finance-pending-row-${rowId}-view-btn`}
                                onClick={() => onViewClick?.(outward)}
                                className="h-8 w-8 rounded-lg hover:bg-gray-200 flex items-center justify-center"
                                title="View details"
                              >
                                <Icon path={mdiEyeOutline} size={0.8} />
                              </button>

                              {actionsAllowed && (
                                <>
                                  {/* Reverse DC gets its own DC + e-way upload,
                                      shown ALONGSIDE Generate DC rather than
                                      replacing it — finance can document the
                                      pickup either way. */}
                                  {"st_subtype" in outward &&
                                    outward.st_subtype === "Reverse DC" && (
                                      <button
                                        id={`warehouse-finance-pending-row-${rowId}-upload-reverse-dc-btn`}
                                        onClick={() =>
                                          onUploadReverseDcDocsClick?.(outward)
                                        }
                                        className="h-8 w-8 rounded-lg hover:bg-blue-200 text-blue-600 flex items-center justify-center"
                                        title="Upload DC & E-way Bill"
                                      >
                                        <Icon
                                          path={mdiUploadOutline}
                                          size={0.8}
                                        />
                                      </button>
                                    )}

                                  {(outward.type === "ST" &&
                                    outward.is_inter_state_transfer === true) ||
                                  outward.type === "SO" ? (
                                    <button
                                      id={`warehouse-finance-pending-row-${rowId}-upload-inv-btn`}
                                      onClick={() =>
                                        onUploadInvClick?.(outward)
                                      }
                                      className="h-8 w-8 rounded-lg hover:bg-blue-200 text-blue-600 flex items-center justify-center"
                                      title="Upload Invoice"
                                    >
                                      <Icon
                                        path={mdiUploadOutline}
                                        size={0.8}
                                      />
                                    </button>
                                  ) : outward.dc_attachment ? (
                                    ""
                                  ) : (
                                    <button
                                      id={`warehouse-finance-pending-row-${rowId}-generate-dc-btn`}
                                      onClick={() =>
                                        onGenerateDCClick?.(outward)
                                      }
                                      className="h-8 w-8 rounded-lg hover:bg-blue-200 text-blue-600 flex items-center justify-center"
                                      title="Generate DC"
                                    >
                                      <Icon
                                        path={mdiFileDocumentOutline}
                                        size={0.8}
                                      />
                                    </button>
                                  )}

                                  {/* Can reject/cancel while still Doc Pending
                                      (even after the DC is received), or when no
                                      DC attachment exists yet. */}
                                  {(outward.status ===
                                    WarehouseOutwardStatus.DOC_PENDING ||
                                    !outward.dc_attachment ||
                                    Object.keys(outward.dc_attachment)
                                      .length === 0) && (
                                    <button
                                      id={`warehouse-finance-pending-row-${rowId}-reject-btn`}
                                      onClick={() => onRejectClick?.(outward)}
                                      className="h-8 w-8 rounded-lg hover:bg-red-200 text-red-600 flex items-center justify-center"
                                      title="Reject Transfer"
                                    >
                                      <Icon
                                        path={mdiCloseCircleOutline}
                                        size={0.8}
                                      />
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>

                      {hasItems && isExpanded && (
                        <tr className="border-b bg-gray-50">
                          <td colSpan={12} className="p-4 space-y-3">
                            {(() => {
                              const shippingAddress =
                                getOutwardDestinationAddress(outward);
                              const courierPartner =
                                outward.shipping_details?.logistic_partner;
                              const trackingId =
                                outward.shipping_details?.tracking_id;
                              if (
                                !shippingAddress &&
                                !courierPartner &&
                                !trackingId
                              )
                                return null;
                              return (
                                <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
                                  <div className="px-4 py-2 text-sm font-semibold text-gray-700 border-b bg-gray-50">
                                    Shipping Details
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 px-4 py-3 text-sm">
                                    <div className="sm:col-span-3">
                                      <div className="text-xs font-medium uppercase tracking-wide text-gray-400">
                                        Shipping Address
                                      </div>
                                      <div className="text-gray-800 mt-0.5">
                                        {shippingAddress
                                          ? formatAddress(shippingAddress)
                                          : "--"}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-xs font-medium uppercase tracking-wide text-gray-400">
                                        Courier Partner
                                      </div>
                                      <div className="text-gray-800 mt-0.5">
                                        {courierPartner || "--"}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-xs font-medium uppercase tracking-wide text-gray-400">
                                        Tracking ID
                                      </div>
                                      <div className="text-gray-800 mt-0.5">
                                        {trackingId || "--"}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                            {items.length > 0 && (
                              <PendingItemsSubTable
                                title={
                                  faultyItems.length > 0
                                    ? "Replacement Items"
                                    : "Items"
                                }
                                rows={items}
                                rowKeyPrefix={`${rowId}-item`}
                                isItemMatch={isItemMatch}
                              />
                            )}
                            {faultyItems.length > 0 && (
                              <PendingItemsSubTable
                                title="Faulty Items"
                                rows={faultyItems}
                                rowKeyPrefix={`${rowId}-faulty`}
                                isItemMatch={isItemMatch}
                              />
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>

        <div className="md:hidden space-y-3">
          {loading && (
            <div className="py-6 text-center text-sm text-gray-500">
              Loading...
            </div>
          )}
          {!loading && outwards.length === 0 && (
            <div className="py-6 text-center text-sm text-gray-500">
              No pending documents found
            </div>
          )}
          {!loading &&
            outwards.map((outward) => {
              const config = getRequiresConfig(outward);
              const totalValue = getTotalValueFromItems(outward);
              const highValue = isHighValue(totalValue);

              const items = outward.items || [];
              const faultyItems = (outward as any).faulty_items || [];
              const hasItems = items.length > 0 || faultyItems.length > 0;
              const hasMatchedItem =
                items.some(isItemMatch) || faultyItems.some(isItemMatch);
              const rowId = outward._id || outward.id;
              const isExpanded =
                expandedRows.has(rowId) || hasMatchedItem || cascade;

              return (
                <MobileCard key={rowId}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 break-words">
                        {outward.type === "SO" ? outward.order_id : outward.id}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-secondary">
                          {outward.type}
                        </span>
                        {outward.type === "ST" &&
                          "st_subtype" in outward &&
                          (outward.st_subtype === "Returnable" ||
                            outward.st_subtype === "Replacement" ||
                            outward.st_subtype === "FoC" ||
                            outward.st_subtype === "Reverse DC") && (
                            <span
                              className={`inline-flex items-center rounded-full border gap-1 font-medium text-[10px] px-1.5 py-0 ${
                                outward.st_subtype === "Replacement"
                                  ? "bg-yellow-100 text-yellow-600 border-yellow-300"
                                  : outward.st_subtype === "FoC"
                                    ? "bg-green-100 text-green-600 border-green-300"
                                    : outward.st_subtype === "Reverse DC"
                                      ? "bg-rose-100 text-rose-700 border-rose-300"
                                      : "bg-blue-100 text-blue-600 border-blue-300"
                              }`}
                            >
                              <RefreshCw className="h-2.5 w-2.5" />
                              {outward.st_subtype}
                            </span>
                          )}
                      </div>
                    </div>
                    <span className="inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 border-purple-200 shrink-0">
                      {outward.status}
                    </span>
                  </div>

                  <div className="divide-y divide-gray-100">
                    <MobileField label="From WH">
                      {getOutwardSourceLabel(outward)}
                    </MobileField>
                    <MobileField label="Destination">
                      {getOutwardDestinationLabel(outward)}
                    </MobileField>
                    <MobileField label="Customer Group">
                      {outward.customerDetails?.customer_group || "--"}
                    </MobileField>
                    <MobileField label="Customer Name">
                      {[
                        outward.customerDetails?.first_name,
                        outward.customerDetails?.last_name,
                      ]
                        .filter(Boolean)
                        .join(" ") || "--"}
                    </MobileField>
                    <MobileField label="Contact">
                      {outward.customerDetails?.mobile || "--"}
                    </MobileField>
                    <MobileField label="Value">
                      <span className={highValue ? "text-yellow-600" : ""}>
                        ₹{totalValue.toLocaleString()}
                        {highValue && (
                          <span
                            className={`block text-xs ${
                              outward.type === "SO" && outward.eway_waived
                                ? "text-gray-500"
                                : "text-yellow-600"
                            }`}
                          >
                            {outward.type === "SO" && outward.eway_waived
                              ? "E-way waived"
                              : "E-way required"}
                          </span>
                        )}
                      </span>
                    </MobileField>
                    <MobileField label="Requires">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.className}`}
                      >
                        <Icon path={config.icon} size={0.6} />
                        {getRequiresLabel(outward)}
                      </span>
                    </MobileField>
                  </div>

                  <div className="mt-3 border-t pt-3 space-y-2">
                    {actionsAllowed && outward.type === "SO" && highValue && (
                      <EwayToggle
                        id={`warehouse-finance-pending-row-${rowId}-mobile-eway-toggle`}
                        outward={outward}
                        onToggle={onToggleEwayClick}
                      />
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        id={`warehouse-finance-pending-row-${rowId}-mobile-view-btn`}
                        onClick={() => onViewClick?.(outward)}
                        className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-100"
                      >
                        <Icon path={mdiEyeOutline} size={0.6} />
                        View
                      </button>
                      {actionsAllowed && (
                        <>
                          {(outward.type === "ST" &&
                            outward.is_inter_state_transfer === true) ||
                          outward.type === "SO" ? (
                            <button
                              id={`warehouse-finance-pending-row-${rowId}-mobile-upload-inv-btn`}
                              onClick={() => onUploadInvClick?.(outward)}
                              className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-blue-600 hover:bg-blue-50"
                            >
                              <Icon path={mdiUploadOutline} size={0.6} />
                              Upload Invoice
                            </button>
                          ) : outward.dc_attachment ? null : (
                            <button
                              id={`warehouse-finance-pending-row-${rowId}-mobile-generate-dc-btn`}
                              onClick={() => onGenerateDCClick?.(outward)}
                              className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-blue-600 hover:bg-blue-50"
                            >
                              <Icon path={mdiFileDocumentOutline} size={0.6} />
                              Generate DC
                            </button>
                          )}
                          {(outward.status ===
                            WarehouseOutwardStatus.DOC_PENDING ||
                            !outward.dc_attachment ||
                            Object.keys(outward.dc_attachment).length ===
                              0) && (
                            <button
                              id={`warehouse-finance-pending-row-${rowId}-mobile-reject-btn`}
                              onClick={() => onRejectClick?.(outward)}
                              className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-red-600 hover:bg-red-50"
                            >
                              <Icon path={mdiCloseCircleOutline} size={0.6} />
                              Reject
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {hasItems && (
                    <button
                      id={`warehouse-finance-pending-row-${rowId}-mobile-expand-btn`}
                      onClick={() => toggleExpanded(rowId)}
                      className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 font-medium"
                    >
                      <Icon
                        path={isExpanded ? mdiChevronDown : mdiChevronRight}
                        size={0.7}
                      />
                      {isExpanded ? "Hide" : "Show"} items
                    </button>
                  )}
                  {hasItems && isExpanded && (
                    <div className="mt-2 space-y-3">
                      {items.length > 0 && (
                        <PendingItemsSubTable
                          title={
                            faultyItems.length > 0
                              ? "Replacement Items"
                              : "Items"
                          }
                          rows={items}
                          rowKeyPrefix={`${rowId}-m-item`}
                          isItemMatch={isItemMatch}
                        />
                      )}
                      {faultyItems.length > 0 && (
                        <PendingItemsSubTable
                          title="Faulty Items"
                          rows={faultyItems}
                          rowKeyPrefix={`${rowId}-m-faulty`}
                          isItemMatch={isItemMatch}
                        />
                      )}
                    </div>
                  )}
                </MobileCard>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default PendingActionsTable;
